import { useCallback, useSyncExternalStore } from 'react';
import { authHeaders } from '@/lib/authHeaders';

/**
 * Per-ACCOUNT UI preferences (widgets, quick-access layout, chosen tabs…) —
 * behaves like usePersistentState but the value lives on the server, so the
 * LAST action from any open session wins and every device follows.
 *
 *  - localStorage caches each value for an instant first paint;
 *  - hydratePrefsFromServer() (app start) pulls the account's stored values
 *    (server wins — it holds the last action from anywhere) and pushes up any
 *    value this browser had that the server never saw (pre-sync migration);
 *  - every set() updates memory + cache and fires a best-effort POST.
 */
const PREFS = new Map<string, unknown>();
let version = 0;
const listeners = new Set<() => void>();
const emit = () => { version++; listeners.forEach((l) => l()); };
const cacheKey = (k: string) => `lexidy-pref:${k}`;

/** Parsed cache values are memoised by their raw text: the same object comes
 *  back until the stored string genuinely changes, so a value read from cache
 *  is as stable as one held in memory. (An unstable snapshot is what took the
 *  portal down for accounts with no roster entry.) */
const parsed = new Map<string, { raw: string; value: unknown }>();
function readCache(key: string): unknown {
  try {
    const raw = localStorage.getItem(cacheKey(key));
    if (raw === null) return undefined;
    const hit = parsed.get(key);
    if (hit && hit.raw === raw) return hit.value;
    const value: unknown = JSON.parse(raw);
    parsed.set(key, { raw, value });
    return value;
  } catch { return undefined; }
}

/* ── WHAT THIS SCREEN CHANGED WINS (Gerónimo, 29 Oct 2026: «que la vista de
   cada página se quede guardada… no está funcionando»). Two ways the last view
   was lost: right after a save the server tells every tab to read the prefs
   again, and that read could bring back the value from BEFORE a second, quicker
   change; and a save that failed (a session being renewed) was never retried,
   so the next load restored the server's old value. Now a key changed here in
   the last few seconds is not overwritten by a read, and a key whose save did
   not land stays «dirty» in this browser — it wins over the server and is sent
   again on the next read. */
const DIRTY_KEY = 'lexidy-pref-dirty';
const FRESH_MS = 15_000;
const changedAt = new Map<string, number>();
function dirtyKeys(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(DIRTY_KEY) || '[]') as string[]); } catch { return new Set(); }
}
function markDirty(key: string, on: boolean) {
  const d = dirtyKeys();
  if (on) d.add(key); else d.delete(key);
  try { localStorage.setItem(DIRTY_KEY, JSON.stringify([...d])); } catch { /* ignore */ }
}

async function pushPref(key: string, value: unknown) {
  markDirty(key, true);
  try {
    const r = await fetch('/api/account/prefs', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ key, value }),
    });
    if (r.ok) markDirty(key, false);
  } catch { /* offline — cache still applies; the next read sends it again */ }
}

/** Pull every stored preference once at app start. Server wins (it holds the
 *  last action from ANY session); cached values the server lacks are pushed. */
export async function hydratePrefsFromServer(): Promise<void> {
  try {
    const r = await fetch('/api/account/prefs', { headers: await authHeaders() });
    if (!r.ok) return;
    const { prefs } = (await r.json()) as { prefs: Record<string, unknown> };
    const dirty = dirtyKeys();
    for (const [k, v] of Object.entries(prefs || {})) {
      if (dirty.has(k) || Date.now() - (changedAt.get(k) || 0) < FRESH_MS) continue;
      PREFS.set(k, v);
      try { localStorage.setItem(cacheKey(k), JSON.stringify(v)); } catch { /* ignore */ }
    }
    for (const k of dirty) {
      const v = readCache(k);
      if (v !== undefined) { PREFS.set(k, v); void pushPref(k, v); }
    }
    // Migration: values this browser already had (old localStorage era) that
    // the server has never seen get pushed up once.
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const full = localStorage.key(i);
        if (!full || !full.startsWith('lexidy-pref:')) continue;
        const k = full.slice('lexidy-pref:'.length);
        if (!(k in (prefs || {}))) {
          const v = readCache(k);
          if (v !== undefined) { PREFS.set(k, v); void pushPref(k, v); }
        }
      }
    } catch { /* ignore */ }
    emit();
  } catch { /* offline — cached values still apply */ }
}

export function setSyncedPref(key: string, value: unknown) {
  changedAt.set(key, Date.now());
  PREFS.set(key, value);
  try { localStorage.setItem(cacheKey(key), JSON.stringify(value)); } catch { /* ignore */ }
  emit();
  void pushPref(key, value);
}

/** Drop-in replacement for usePersistentState, but account-synced.
 *  `legacy` optionally reads a pre-existing local value to migrate from. */
export function useSyncedPref<T>(key: string, def: T, legacy?: () => T | undefined): [T, (v: T) => void] {
  useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => version,
    () => version,
  );
  let value: T;
  if (PREFS.has(key)) value = PREFS.get(key) as T;
  else {
    const cached = readCache(key);
    if (cached !== undefined) value = cached as T;
    else {
      const lg = legacy?.();
      value = lg !== undefined ? lg : def;
    }
  }
  const set = useCallback((v: T) => setSyncedPref(key, v), [key]);
  return [value, set];
}
